import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { cargasApi, type Carga, type ForensicReport } from '../api/cargas'
import { Table, type Column } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { GenericBadge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'

function fmt(n: unknown, dec = 1) {
  if (n === null || n === undefined || n === '') return '—'
  const num = typeof n === 'number' ? n : parseFloat(String(n))
  if (!Number.isFinite(num)) return '—'
  return num.toFixed(dec)
}

export default function Outliers() {
  const navigate = useNavigate()

  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [modalCargaId, setModalCargaId] = useState<string | null>(null)
  const [forensicResult, setForensicResult] = useState<ForensicReport | null>(null)
  const [forensicError, setForensicError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['outliers', desde, hasta],
    queryFn: () => cargasApi.outliers(desde || undefined, hasta || undefined),
  })

  const forensicMutation = useMutation({
    mutationFn: (cargaId: string) => cargasApi.requestForensic(cargaId),
    onMutate: () => {
      setForensicResult(null)
      setForensicError(null)
    },
    onSuccess: (data) => {
      setForensicResult(data)
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error al obtener análisis IA'
      setForensicError(msg)
    },
  })

  function handleForensic(cargaId: string) {
    setModalCargaId(cargaId)
    setForensicResult(null)
    setForensicError(null)
    setModalOpen(true)
    forensicMutation.mutate(cargaId)
  }

  const items = data?.items ?? []

  const COLUMNS: Column<Carga>[] = [
    {
      key: 'creadoAt',
      header: 'Fecha',
      sortable: true,
      render: (row) =>
        new Date(row.creadoAt).toLocaleString('es-BO', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
    {
      key: 'vehiculo',
      header: 'Vehículo',
      render: (row) => row.vehiculo?.codigo ?? row.vehiculoId.slice(0, 8),
    },
    {
      key: 'operador',
      header: 'Operador',
      render: (row) => row.operador?.nombre ?? row.operadorId.slice(0, 8),
    },
    {
      key: 'litros',
      header: 'Litros',
      sortable: true,
      render: (row) => `${Number(row.litros).toFixed(0)} L`,
      className: 'tabular-nums',
    },
    {
      key: 'consumoLitrosHora',
      header: 'Consumo L/h',
      render: (row) => fmt(row.consumoLitrosHora),
      className: 'tabular-nums',
    },
    {
      key: 'outlierFlags',
      header: 'Flags',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.outlierFlags && row.outlierFlags.length > 0 ? (
            row.outlierFlags.map((flag) => (
              <GenericBadge key={flag} label={flag} variant="warning" />
            ))
          ) : (
            <span className="text-slate-400 text-xs">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'estadoValidacion',
      header: 'Estado',
      render: () => <Badge estado="OUTLIER" />,
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/cargas/${row.id}`)
            }}
            className="text-xs text-primary hover:underline font-medium whitespace-nowrap"
          >
            Ver detalle
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleForensic(row.id)
            }}
            className="text-xs text-violet-600 dark:text-violet-400 hover:underline font-medium whitespace-nowrap"
          >
            Análisis IA
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Outliers</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Cargas con anomalías detectadas automaticamente
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <Input
          type="date"
          label="Desde"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <Input
          type="date"
          label="Hasta"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        {(desde || hasta) && (
          <button
            onClick={() => { setDesde(''); setHasta('') }}
            className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 self-end pb-2"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Counter */}
      {!isLoading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {items.length > 0
            ? `${items.length} outlier${items.length !== 1 ? 's' : ''} encontrado${items.length !== 1 ? 's' : ''}`
            : ''}
        </p>
      )}

      {/* Table */}
      <Table
        columns={COLUMNS}
        data={items}
        keyExtractor={(r) => r.id}
        loading={isLoading}
        emptyMessage="Sin outliers en el período — todo parece normal"
        onRowClick={(row) => navigate(`/cargas/${row.id}`)}
      />

      {/* Forensic Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setModalCargaId(null)
        }}
        title="Análisis IA Forense"
        size="lg"
      >
        <div className="space-y-4">
          {forensicMutation.isPending && (
            <div className="flex flex-col items-center py-8 gap-3">
              <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <p className="text-sm text-slate-500 dark:text-slate-400">Analizando carga con IA...</p>
            </div>
          )}

          {forensicError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{forensicError}</p>
            </div>
          )}

          {forensicResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Probabilidad de fraude</p>
                  <p className={`text-2xl font-bold ${
                    forensicResult.probabilidadFraude > 0.7
                      ? 'text-red-600 dark:text-red-400'
                      : forensicResult.probabilidadFraude > 0.4
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }`}>
                    {(forensicResult.probabilidadFraude * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Modelo IA</p>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 break-all">
                    {forensicResult.modeloIA ?? 'claude-sonnet-4-6'}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  Recomendación
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">{forensicResult.recomendacion}</p>
              </div>

              {forensicResult.hallazgos && Object.keys(forensicResult.hallazgos).length > 0 && (
                <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    Hallazgos
                  </p>
                  <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap overflow-auto max-h-48">
                    {JSON.stringify(forensicResult.hallazgos, null, 2)}
                  </pre>
                </div>
              )}

              <p className="text-xs text-slate-400 text-right">
                Carga: {modalCargaId?.slice(0, 8)}...
                {forensicResult.creadoAt && (
                  <> · {new Date(forensicResult.creadoAt).toLocaleString('es-BO')}</>
                )}
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setModalOpen(false)
                setModalCargaId(null)
              }}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
